/**
 * 货币格式转换工具
 * 处理前端货币格式（'RMB', 'HK$', 'US$' 等）与后端 UserCurrency 枚举值的转换
 */

import { UserCurrency } from '../../../../types/auth';

/**
 * 前端货币格式类型
 */
export type FrontendCurrency = 'RMB' | 'HK$' | 'US$' | 'EUR' | 'JPY' | 'GBP' | 'CAD' | 'AUD';

/**
 * 将前端货币格式转换为后端 UserCurrency 枚举值
 * @param frontendCurrency 前端货币格式（如 'RMB', 'HK$'）
 * @returns 后端 UserCurrency 枚举值，如果不支持则返回 null
 */
export function convertToBackendCurrency(frontendCurrency: string): UserCurrency | null {
  const currencyMap: Record<FrontendCurrency, UserCurrency | null> = {
    'RMB': UserCurrency.CNY,  // 人民币
    'HK$': UserCurrency.HKD,   // 港币
    'US$': UserCurrency.USD,   // 美元
    'EUR': UserCurrency.EUR,   // 欧元
    'JPY': UserCurrency.JPY,   // 日元
    'GBP': UserCurrency.GBP,   // 英镑
    'CAD': null,               // 加元 - 后端不支持，返回 null
    'AUD': null,               // 澳元 - 后端不支持，返回 null
  };

  return currencyMap[frontendCurrency as FrontendCurrency] ?? null;
}

/**
 * 将后端 UserCurrency 枚举值转换为前端货币格式
 * @param backendCurrency 后端 UserCurrency 枚举值（如 '人民币', '港币'）
 * @returns 前端货币格式，如果不支持则返回 'RMB' 作为默认值
 */
export function convertToFrontendCurrency(backendCurrency: UserCurrency | null | undefined): FrontendCurrency {
  if (!backendCurrency) {
    return 'RMB'; // 默认值
  }

  const currencyMap: Record<UserCurrency, FrontendCurrency> = {
    [UserCurrency.CNY]: 'RMB',  // 人民币
    [UserCurrency.HKD]: 'HK$',  // 港币
    [UserCurrency.USD]: 'US$',  // 美元
    [UserCurrency.EUR]: 'EUR',  // 欧元
    [UserCurrency.JPY]: 'JPY',  // 日元
    [UserCurrency.GBP]: 'GBP',  // 英镑
    [UserCurrency.TWD]: 'RMB',   // 新台币 - 前端不支持，映射到 RMB
    [UserCurrency.KRW]: 'RMB',   // 韩元 - 前端不支持，映射到 RMB
  };

  return currencyMap[backendCurrency] ?? 'RMB'; // 默认值
}

const USER_CURRENCY_VALUES = new Set<string>(Object.values(UserCurrency));

/**
 * userAppConfig.currency（ISO 代码如 CNY）→ 预算表 budgetItemCurrency（RMB、HK$ 等）
 */
export function appCurrencyCodeToBudgetCurrency(currencyCode: string): FrontendCurrency {
  const map: Record<string, FrontendCurrency> = {
    CNY: 'RMB',
    HKD: 'HK$',
    TWD: 'RMB',
    USD: 'US$',
    JPY: 'JPY',
    KRW: 'RMB',
    GBP: 'GBP',
    EUR: 'EUR',
    THB: 'RMB',
  };
  const upper = currencyCode?.trim().toUpperCase();
  if (upper && map[upper]) return map[upper];
  if (USER_CURRENCY_VALUES.has(currencyCode)) {
    return convertToFrontendCurrency(currencyCode as UserCurrency);
  }
  return 'RMB';
}

/** @deprecated 使用 appCurrencyCodeToBudgetCurrency；仍兼容中文货币名 */
export function homePreferredCurrencyIdToBudgetCurrency(homeCurrencyId: string): FrontendCurrency {
  return appCurrencyCodeToBudgetCurrency(homeCurrencyId);
}
